import axios from 'axios';
import base_url from '../util/baseUrl';
import { getUserToken, getUserRefreshToken, logoutUser, notifyUser } from '../util/util';
import secureLocalStorage from 'react-secure-storage';

// Define routes that don't require authentication
const UNAUTHENTICATED_ROUTES = [
  'login',
  'verify-email',
  'verify-otp',
  'create-password',
  'register',
  'forgot-password',
  'reset-password',
  'resend-verification',
  'self-service/forgot-password',
  'self-service/verify-reset-otp',
  'help/contact',
  'help/support'
];

// Helper function to check if a route requires authentication
const requiresAuthentication = (url) => {
  return !UNAUTHENTICATED_ROUTES.some(route => url.includes(route));
};

// Track ongoing refresh to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Create axios instance
const apiClient = axios.create({
  baseURL: base_url,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getUserToken();
    const needsAuth = requiresAuthentication(config.url);
    
    // Only add Authorization header if token exists AND route requires authentication
    if (token && needsAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const needsAuth = requiresAuthentication(originalRequest.url);
    
    // Handle 401 errors for authenticated routes
    if (error.response?.status === 401 && needsAuth && !originalRequest._retry) {
      const refreshToken = getUserRefreshToken();
      
      // If no refresh token, logout user
      if (!refreshToken) {
        notifyUser("error", "Session expired. Please log in again.", "Error");
        logoutUser();
        return Promise.reject(error);
      }
      
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const response = await axios.post(`${base_url}token/refresh`, 
          { tokenValue: refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${getUserToken()}`,
            },
          }
        );
        
        if (response.data?.success) {
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          
          // Update tokens
          secureLocalStorage.setItem("token", accessToken);
          secureLocalStorage.setItem("refreshToken", newRefreshToken);
          
          // Process queued requests
          processQueue(null, accessToken);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        } else {
          throw new Error(response.data?.message || "Token refresh failed");
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        notifyUser("error", "Session expired. Please log in again.", "Error");
        logoutUser();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

// Helper functions for different HTTP methods
export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};

// File upload helper
export const uploadFile = (url, formData, config = {}) => {
  return apiClient.post(url, formData, {
    ...config,
    headers: {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export { apiClient };
